import React, { Component } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import AJAX from 'common/AJAX';
import NoteBox from 'common/common/NoteBox';
import Tooltip from 'common/common/Tooltip';
import { CompanyContext } from 'common/containers/CompanyContainer';
import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';
import EncryptSSOCSharpSnippet from 'common/developers/docs/widget/snippets/EncryptSSOCSharpSnippet';
import EncryptSSOGoSnippet from 'common/developers/docs/widget/snippets/EncryptSSOGoSnippet';
import EncryptSSOJavaSnippet from 'common/developers/docs/widget/snippets/EncryptSSOJavaSnippet';
import EncryptSSONodeSnippet from 'common/developers/docs/widget/snippets/EncryptSSONodeSnippet';
import EncryptSSOPHPSnippet from 'common/developers/docs/widget/snippets/EncryptSSOPHPSnippet';
import EncryptSSOPythonSnippet from 'common/developers/docs/widget/snippets/EncryptSSOPythonSnippet';
import EncryptSSORubySnippet from 'common/developers/docs/widget/snippets/EncryptSSORubySnippet';
import InstallJWTCSharpSnippet from 'common/developers/docs/widget/snippets/InstallJWTCSharpSnippet';
import InstallJWTGoSnippet from 'common/developers/docs/widget/snippets/InstallJWTGoSnippet';
import InstallJWTJavaSnippet from 'common/developers/docs/widget/snippets/InstallJWTJavaSnippet';
import InstallJWTNodeSnippet from 'common/developers/docs/widget/snippets/InstallJWTNodeSnippet';
import InstallJWTPHPSnippet from 'common/developers/docs/widget/snippets/InstallJWTPHPSnippet';
import InstallJWTPythonSnippet from 'common/developers/docs/widget/snippets/InstallJWTPythonSnippet';
import InstallJWTRubySnippet from 'common/developers/docs/widget/snippets/InstallJWTRubySnippet';
import WidgetDocsWarning from 'common/developers/docs/widget/WidgetDocsWarning';
import WidgetDocsSSOHelmet from 'common/helmets/WidgetDocsSSOHelmet';
import Button from 'common/inputs/Button';
import Link from 'common/Link';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import 'css/components/developers/docs/widget/_WidgetDocsSSO.scss';

function getSnippets(company) {
  if (company && company.privateSSOKey) {
    return {
      csharp: EncryptSSOCSharpSnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
      go: EncryptSSOGoSnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
      java: EncryptSSOJavaSnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
      node: EncryptSSONodeSnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
      php: EncryptSSOPHPSnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
      python: EncryptSSOPythonSnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
      ruby: EncryptSSORubySnippet.replace('YOUR_PRIVATE_SSO_KEY', company.privateSSOKey),
    };
  } else {
    return {
      csharp: EncryptSSOCSharpSnippet,
      go: EncryptSSOGoSnippet,
      java: EncryptSSOJavaSnippet,
      node: EncryptSSONodeSnippet,
      php: EncryptSSOPHPSnippet,
      python: EncryptSSOPythonSnippet,
      ruby: EncryptSSORubySnippet,
    };
  }
}

class WidgetDocsSSO extends Component {
  static propTypes = {
    company: PropTypes.shape({
      privateSSOKey: PropTypes.string,
    }),
  };

  state = {
    error: null,
    isLoading: false,
    validated: false,
  };

  constructor(props, context) {
    super(props, context);

    this.tokenTextareaRef = React.createRef();
  }

  verifyToken = async () => {
    const { company } = this.props;
    const token = this.tokenTextareaRef.current.value;
    if (!token) {
      this.setState({
        error: 'You have to enter a token to be able to validate it',
        validated: false,
        isLoading: false,
      });
      return;
    }

    this.setState({ error: null, isLoading: true, validated: false });

    const response = await AJAX.post('/api/developers/validateSSO', {
      companyID: company._id,
      token,
    });

    const keyMessage = company?.privateSSOKey
      ? `key ${company.privateSSOKey} for ${company.name},`
      : `correct key for ${company?.name ?? 'your company'},`;
    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        'invalid avatar url':
          'The "avatar" field should be either omitted or a string URL pointing to a valid image.',
        'invalid companies':
          'The "companies" field should be either null or an array of valid company objects',
        'invalid company created date':
          'One of the companies has an invalid "created" field. It should be a valid ISO 8601 Date (e.g. "2021-04-27T14:11:34.488Z")',
        'invalid company custom fields':
          'One of the companies has an invalid "customFields" field.',
        'invalid company id':
          'One of the companies has an invalid "id" field. The id is a required field, and should be a string.',
        'invalid company monthly spend':
          'One of the companies has an invalid "monthlySpend" field. The field should be a number.',
        'invalid company name':
          'One of the companies has an invalid company name. This field is required, and should be a string of reasonable lenght.',
        'invalid created date':
          'The user created date is invalid. It should be a valid ISO 8601 Date (e.g. 2021-04-27T14:11:34.488Z).',
        'invalid key': `Either the key or the algorithm used to sign this token was invalid. Make sure you are using the ${keyMessage} and the algorithm HS256.`,
        'invalid user id': 'The user ID provided is invalid.',
        'invalid user email': 'The user email provided is invalid.',
        'invalid user name': 'The user name provided is invalid.',
        'is admin user':
          "This user is a Canny admin for another company, so they can't be authenticated with an SSO token.",
        'unauthorized user':
          "You can't validate SSO tokens. Please login refresh the page and confirm your account is connected to a company.",
      },
    });

    this.setState({ error: error?.message, isLoading: false, validated: !error });
  };

  renderButton = () => {
    const { company } = this.props;
    if (!company?.privateSSOKey) {
      return (
        <Tooltip position="top" value="Please log in to verify a token">
          <Button disabled={true} value="Verify token" />
        </Tooltip>
      );
    }

    return <Button loading={this.state.isLoading} onTap={this.verifyToken} value="Verify token" />;
  };

  renderError = () => {
    const { error } = this.state;
    if (!error) {
      return;
    }

    return <NoteBox className="errorNote">{error}</NoteBox>;
  };

  renderSuccess = () => {
    const { validated } = this.state;
    if (!validated) {
      return;
    }

    return <NoteBox className="successNote">✓ Token validated successfully</NoteBox>;
  };

  render() {
    const { company } = this.props;
    const { error, validated } = this.state;
    const encryptSnippets = getSnippets(company);
    return (
      <div className="widgetDocsSSO">
        <WidgetDocsSSOHelmet />
        <WidgetDocsWarning />
        <div className="title">Single Sign-On</div>
        <div className="intro">
          These are instructions on how to generate Single Sign-On tokens on your server. These
          token can be used to authenticate your users in our web/mobile&nbsp;widget.
        </div>
        <ol className="numberedList">
          <li>
            <div className="subheading">Here's how it&nbsp;works:</div>
            <div className="contents">
              <ul className="tight">
                <li>
                  {company && company.privateSSOKey ? (
                    <div className="contents">
                      This is your private key:&nbsp;
                      <span className="emphasize">{company.privateSSOKey}</span>
                    </div>
                  ) : (
                    <div className="contents">To find your private key, log in&nbsp;above.</div>
                  )}
                  Store your private key on your server and don't share&nbsp;it.
                </li>
                <li>
                  When a user wants to use the widget, send a request to your server to generate an
                  SSO&nbsp;token.
                </li>
                <li>On your server, generate a token using the snippet&nbsp;below.</li>
                <li>Pass the token back to your app and into our&nbsp;widget.</li>
                <li>We'll use that token to authenticate your&nbsp;user.</li>
              </ul>
              <NoteBox className="note">
                <div className="text">
                  For security reasons, Single Sign-On tokens can't authenticate users who are admin
                  users for other companies. These users will have to log in using&nbsp;
                  <a href="https://canny.io/login" rel="noopener noreferrer" target="_blank">
                    https://canny.io/login
                  </a>
                </div>
              </NoteBox>
            </div>
          </li>
          <li>
            <div className="subheading">1. Install a JWT library</div>
            <div className="contents">
              We use JSON Web Tokens to securely authenticate your users. First, install the
              appropriate JWT library for your server.
            </div>
            <MultiLanguageCodeBlock
              blocks={[
                {
                  name: 'Node.js',
                  language: 'bash',
                  code: InstallJWTNodeSnippet,
                },
                {
                  name: 'C#',
                  language: 'bash',
                  code: InstallJWTCSharpSnippet,
                },
                {
                  name: 'Go',
                  language: 'bash',
                  code: InstallJWTGoSnippet,
                },
                {
                  name: 'Java',
                  language: 'bash',
                  code: InstallJWTJavaSnippet,
                },
                {
                  name: 'PHP',
                  language: 'bash',
                  code: InstallJWTPHPSnippet,
                },
                {
                  name: 'Python',
                  language: 'bash',
                  code: InstallJWTPythonSnippet,
                },
                {
                  name: 'Ruby',
                  language: 'bash',
                  code: InstallJWTRubySnippet,
                },
              ]}
            />
          </li>
          <li>
            <div className="subheading">2. Generate tokens on your&nbsp;server</div>
            <div className="contents">
              <MultiLanguageCodeBlock
                blocks={[
                  {
                    name: 'Node.js',
                    language: 'js',
                    code: encryptSnippets.node,
                  },
                  {
                    name: 'C#',
                    language: 'csharp',
                    code: encryptSnippets.csharp,
                  },
                  {
                    name: 'Go',
                    language: 'go',
                    code: encryptSnippets.go,
                  },
                  {
                    name: 'Java',
                    language: 'java',
                    code: encryptSnippets.java,
                  },
                  {
                    name: 'PHP',
                    language: 'php',
                    code: encryptSnippets.php,
                  },
                  {
                    name: 'Python',
                    language: 'python',
                    code: encryptSnippets.python,
                  },
                  {
                    name: 'Ruby',
                    language: 'ruby',
                    code: encryptSnippets.ruby,
                  },
                ]}
              />
            </div>
            <div className="contents">
              Any field you can specify via identify / our API also works via SSO tokens. Consult{' '}
              <Link to="/install/companies">this page</Link> for a full list of fields.
            </div>
          </li>
          <li>
            <div className="subheading">3. Validate your SSO Token</div>
            <div className="contents">
              Paste a sample SSO token in the following input to verify it will work correctly.
            </div>
            <input
              ref={this.tokenTextareaRef}
              className={classnames('tokenTextarea', { failed: error, validated })}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5c..."
              type="text"
            />
            {this.renderButton()}
            {this.renderError()}
            {this.renderSuccess()}
          </li>
          <li>
            <div className="subheading">Questions</div>
            <div className="contents">
              If you have any questions or issues, email us at{' '}
              <a href="mailto:support@canny.io">support@canny.io</a> and we'll get back to
              you&nbsp;ASAP.
            </div>
          </li>
        </ol>
      </div>
    );
  }
}

export default compose(withContexts({ company: CompanyContext }))(WidgetDocsSSO);
